import React from "react";
import {
  ChevronLeft,
  ChevronRight,
  Database,
  Pencil,
  Shield,
  Trash2,
} from "lucide-react";
import type { InformationTypeResponse } from "../../../../api/frameworksApi";
import {
  FormField,
  FormInput,
  FormSelect,
} from "../../../components/Modal";
import { Tip } from "../../../components/ui/tooltip";
import { LoadingSpinner } from "../components/LoadingSpinner";
import { ImpactPill } from "../components/SysDisplay";
import { getPageWindow } from "../utils/pagination";

export interface InformationTypesTabProps {
  infoTypesLoading: boolean;
  infoTypes: InformationTypeResponse[];
  pagInfoTypes: InformationTypeResponse[];
  filtInfoTypes: InformationTypeResponse[];
  editingInfoTypeId: string | null;
  setEditingInfoTypeId: React.Dispatch<React.SetStateAction<string | null>>;
  editInfoTypeForm: {
    title: string;
    description: string;
    confidentiality: string;
    integrity: string;
    availability: string;
  };
  setEditInfoTypeForm: React.Dispatch<
    React.SetStateAction<{
      title: string;
      description: string;
      confidentiality: string;
      integrity: string;
      availability: string;
    }>
  >;
  savingInfoType: boolean;
  handleUpdateInfoType: () => void;
  isPublishedSsp: boolean;
  requestDelete: (
    type: string,
    id: string,
    title: string,
    action: () => Promise<void>,
  ) => void;
  handleDeleteInfoType: (infoTypeId: string) => Promise<void>;
  tabPage: number;
  tabPageSize: number;
  setCurrentTabPagination: (pagination: {
    page: number;
    pageSize: number;
  }) => void;
}

export function InformationTypesTab({
  infoTypesLoading,
  infoTypes,
  pagInfoTypes,
  filtInfoTypes,
  editingInfoTypeId,
  setEditingInfoTypeId,
  editInfoTypeForm,
  setEditInfoTypeForm,
  savingInfoType,
  handleUpdateInfoType,
  isPublishedSsp,
  requestDelete,
  handleDeleteInfoType,
  tabPage,
  tabPageSize,
  setCurrentTabPagination,
}: InformationTypesTabProps) {
  return (
    <>
      {infoTypesLoading ? (
        <LoadingSpinner text="Loading information types" />
      ) : infoTypes.length > 0 ? (
        <>
          <div className="rounded-lg border border-border bg-card overflow-hidden">
            {/* Header */}
            <div className="hidden md:grid grid-cols-[52px_1fr_400px_44px] gap-4 px-5 py-3.5 border-b border-border bg-muted/30">
              <div />
              <div>
                <p className="text-[13px] font-medium text-[#1e293b] dark:text-white">
                  Information Type
                </p>
              </div>
              <div className="grid grid-cols-3 gap-2 text-[13px] font-medium text-[#1e293b] dark:text-white text-center">
                <span>Confidentiality</span>
                <span>Integrity</span>
                <span>Availability</span>
              </div>
              <div />
            </div>

            <div className="divide-y divide-border">
              {pagInfoTypes.map((it) => (
                <div key={it.id} className="px-5 py-3.5 hover:bg-muted/40 transition-colors">
                  {editingInfoTypeId === it.id ? (
                    <div className="space-y-3">
                      <div className="grid grid-cols-2 gap-3">
                        <FormField label="Title" required>
                          <FormInput
                            value={editInfoTypeForm.title}
                            onChange={(v) => setEditInfoTypeForm((f) => ({ ...f, title: v }))}
                            placeholder="Information type title"
                          />
                        </FormField>
                        <FormField label="Description">
                          <FormInput
                            value={editInfoTypeForm.description}
                            onChange={(v) => setEditInfoTypeForm((f) => ({ ...f, description: v }))}
                            placeholder="Description"
                          />
                        </FormField>
                      </div>
                      <div className="grid grid-cols-3 gap-3">
                        {(["confidentiality", "integrity", "availability"] as const).map((field) => (
                          <FormField key={field} label={field.charAt(0).toUpperCase() + field.slice(1)}>
                            <FormSelect
                              options={[
                                { label: "Low", value: "low" },
                                { label: "Moderate", value: "moderate" },
                                { label: "High", value: "high" },
                              ]}
                              value={editInfoTypeForm[field]}
                              onChange={(v) => setEditInfoTypeForm((f) => ({ ...f, [field]: v }))}
                              placeholder="Select level"
                            />
                          </FormField>
                        ))}
                      </div>
                      <div className="flex items-center gap-2 pt-1">
                        <button
                          onClick={() => setEditingInfoTypeId(null)}
                          className="px-3 py-1.5 rounded-lg border border-border text-xs font-medium hover:bg-accent transition-colors"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleUpdateInfoType}
                          disabled={!editInfoTypeForm.title || savingInfoType}
                          className="px-3 py-1.5 rounded-lg bg-indigo-600 text-white text-xs font-bold hover:bg-indigo-700 transition-colors disabled:opacity-50"
                        >
                          {savingInfoType ? "Saving" : "Save"}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="grid grid-cols-[40px_1fr] md:grid-cols-[52px_1fr_400px_44px] gap-3 md:gap-4 items-center">
                      <div className="flex items-center justify-center size-10 rounded-lg bg-indigo-50 dark:bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 flex-shrink-0">
                        <Database className="size-5" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-foreground">{it.title}</p>
                        {it.description && (
                          <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{it.description}</p>
                        )}
                      </div>
                      <div className="grid grid-cols-3 gap-2 col-span-2 md:col-span-1">
                        {(["confidentiality", "integrity", "availability"] as const).map((field) => {
                          const impact = field === "confidentiality" ? it.confidentialityImpact : field === "integrity" ? it.integrityImpact : it.availabilityImpact;
                          return (
                            <div key={field} className="flex justify-center">
                              <ImpactPill level={impact?.base} />
                            </div>
                          );
                        })}
                      </div>
                      {!isPublishedSsp ? (
                        <div className="flex items-center gap-1 justify-end flex-shrink-0 col-span-2 md:col-span-1">
                          <Tip label="Edit Information Type" side="bottom">
                            <button
                              onClick={() => {
                                setEditingInfoTypeId(it.id);
                                setEditInfoTypeForm({
                                  title: it.title,
                                  description: it.description || "",
                                  confidentiality: it.confidentialityImpact?.base || "",
                                  integrity: it.integrityImpact?.base || "",
                                  availability: it.availabilityImpact?.base || "",
                                });
                              }}
                              className="p-1.5 rounded-md hover:bg-indigo-50 dark:hover:bg-indigo-500/10 text-muted-foreground hover:text-indigo-600 transition-colors"
                            >
                              <Pencil className="size-3.5 hover:text-indigo-600" />
                            </button>
                          </Tip>
                          <Tip label="Delete Information Type" side="bottom">
                            <button
                              onClick={() => requestDelete("Information Type", it.id, it.title, () => handleDeleteInfoType(it.id))}
                              className="p-1.5 rounded-md hover:bg-red-50 dark:hover:bg-red-500/10 text-muted-foreground hover:text-red-600 transition-colors"
                            >
                              <Trash2 className="size-3.5 hover:text-red-600" />
                            </button>
                          </Tip>
                        </div>
                      ) : null}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
          {filtInfoTypes.length > 0 && (
            <div className="flex items-center justify-between gap-2 px-4 py-2 border-t border-[#e2e8f0] dark:border-white/[0.06] bg-[#fafbfc] dark:bg-white/[0.015] flex-shrink-0">
              <p className="text-[11px] leading-tight text-[#475569] dark:text-white/70">
                Showing{" "}
                <span className="font-semibold text-[#334155] dark:text-white/90">
                  {Math.min((tabPage - 1) * tabPageSize + 1, filtInfoTypes.length)}–{Math.min(tabPage * tabPageSize, filtInfoTypes.length)}
                </span>{" "}
                of{" "}
                <span className="font-semibold text-[#334155] dark:text-white/90">
                  {filtInfoTypes.length}
                </span>
              </p>
              {Math.ceil(filtInfoTypes.length / tabPageSize) > 1 && (
                <div className="flex items-center gap-0.5">
                  <button
                    disabled={tabPage === 1}
                    onClick={() => setCurrentTabPagination({ page: Math.max(1, tabPage - 1), pageSize: tabPageSize })}
                    className="p-1 rounded-md border border-[#e2e8f0] dark:border-white/[0.07] text-[#475569] dark:text-white/70 hover:bg-[#f1f5f9] dark:hover:bg-white/[0.04] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronLeft className="size-3" />
                  </button>

                  {getPageWindow(tabPage, Math.ceil(filtInfoTypes.length / tabPageSize)).map((p, idx) =>
                    p === "dots" ? (
                      <span
                        key={`dots-${idx}`}
                        className="w-6 h-6 flex items-center justify-center text-xs text-[#475569] dark:text-white/70 select-none"
                      >
                        …
                      </span>
                    ) : (
                      <button
                        key={p}
                        onClick={() => setCurrentTabPagination({ page: p as number, pageSize: tabPageSize })}
                        className={`w-6 h-6 text-xs font-medium rounded-md transition-colors ${p === tabPage
                          ? "bg-indigo-600 dark:bg-indigo-600 text-white"
                          : "text-[#475569] dark:text-white/70 hover:bg-[#f1f5f9] dark:hover:bg-white/[0.04]"
                          }`}
                      >
                        {p}
                      </button>
                    ),
                  )}

                  <button
                    disabled={tabPage >= Math.ceil(filtInfoTypes.length / tabPageSize)}
                    onClick={() => setCurrentTabPagination({ page: Math.min(Math.ceil(filtInfoTypes.length / tabPageSize), tabPage + 1), pageSize: tabPageSize })}
                    className="p-1 rounded-md border border-[#e2e8f0] dark:border-white/[0.07] text-[#475569] dark:text-white/70 hover:bg-[#f1f5f9] dark:hover:bg-white/[0.04] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronRight className="size-3" />
                  </button>
                </div>
              )}
            </div>
          )}
        </>
      ) : (
        <div className="rounded-lg border border-border bg-card p-5 text-center py-12">
          <Shield className="size-8 text-muted-foreground mb-3 mx-auto" />
          <p className="text-sm font-medium">
            No information types defined
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            Add information types processed by this system
          </p>
        </div>
      )}
    </>
  );
}
